/**
 * Asset Configuration Constants
 * Keep in sync with app/models/concerns/asset_constraints.rb
 */

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exp = Math.floor(Math.log(bytes) / Math.log(1024));
  const index = Math.max(0, Math.min(exp, units.length - 1));
  return `${(bytes / 1024 ** index).toFixed(2)} ${units[index]}`;
}

export type AssetFileTypes = 'all' | 'images' | 'videos';

const ALL_ACCEPT_ATTRIBUTES: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/svg+xml': ['.svg'],
  'image/webp': ['.webp'],
  'image/x-icon': ['.ico'],
  'image/vnd.microsoft.icon': ['.ico'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
  'video/webm': ['.webm'],
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/ogg': ['.ogg'],
  'audio/aac': ['.aac'],
  'audio/flac': ['.flac'],
  'audio/mp4': ['.m4a'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls', '.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt', '.csv'],
  'application/rtf': ['.rtf'],
  'text/rtf': ['.rtf'],
  'font/woff': ['.woff'],
  'font/woff2': ['.woff2'],
  'font/ttf': ['.ttf'],
  'font/otf': ['.otf'],
  'application/font-woff': ['.woff'],
  'application/x-font-ttf': ['.ttf'],
  'application/x-font-otf': ['.otf'],
};

export function getAcceptAttribute(fileTypes: AssetFileTypes = 'all'): Record<string, string[]> {
  if (fileTypes === 'images') {
    return Object.fromEntries(
      Object.entries(ALL_ACCEPT_ATTRIBUTES).filter(([mimeType]) => mimeType.startsWith('image/')),
    );
  }
  if (fileTypes === 'videos') {
    return Object.fromEntries(
      Object.entries(ALL_ACCEPT_ATTRIBUTES).filter(([mimeType]) => mimeType.startsWith('video/')),
    );
  }
  return ALL_ACCEPT_ATTRIBUTES;
}

export function getSupportedFileTypesText(fileTypes: AssetFileTypes = 'all'): string {
  if (fileTypes === 'images') return 'Images (JPG, JPEG, PNG, GIF, SVG, WEBP or ICO)';
  if (fileTypes === 'videos') return 'Videos (MP4, MOV, AVI or WEBM)';
  return 'Images, Videos, Audio, Documents and Fonts';
}

export function getValidExtensionsForContentType(contentType: string | undefined): string[] {
  return ALL_ACCEPT_ATTRIBUTES[contentType?.toLowerCase() ?? ''] ?? [];
}

export function isSvgImage(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;

  const image = value as { contentType?: unknown; url?: unknown };
  if (image.contentType === 'image/svg+xml') return true;
  if (typeof image.url !== 'string') return false;

  const [urlWithoutQueryOrHash] = image.url.split(/[?#]/, 1);
  return urlWithoutQueryOrHash.toLowerCase().endsWith('.svg');
}

export function isGifImage(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    'contentType' in value &&
    (value as { contentType: unknown }).contentType === 'image/gif'
  );
}
